import { useState, useMemo, useEffect } from 'react';
import { C, iS, fmtTime, makeCode, PROGRAM_MAP } from './config.js';
import { API } from './api.js';
import { Btn } from './common.jsx';
import { EditCourseModal, AddParticipantModal } from './CourseModals.jsx';

export default function DetailPanel({ course, onClose, toast }) {
  const [ptcps,    setPtcps]    = useState([]);
  const [pLoad,    setPLoad]    = useState(false);
  const [codes,    setCodes]    = useState([]);
  const [cLoad,    setCLoad]    = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showAddP, setShowAddP] = useState(false);

  const prog = course._prog;

  /* 게임 진행자 로드 — introInCode prefix + createdAt 날짜 두 조건 */
  const loadPtcps = async () => {
    if (!course.tableId || !prog) { setPtcps([]); return; }
    setPLoad(true);
    try {
      const BASE    = "https://parseapi.back4app.com";
      const headers = {
        "X-Parse-Application-Id": prog.appId,
        "X-Parse-REST-API-Key":   prog.apiKey || prog.restKey || prog.jsKey,
      };

      const roundAlpha = String.fromCharCode(96 + Number(course.round));
      const codeField  = prog.codeField || "introInCode";

      let whereObj = {};

      if (prog.dateField === "use_date") {
        // 스트레스버그: entry_code prefix + createdAt 날짜(KST) 필터
        const codePrefix = `${prog.prefix}${roundAlpha}`;
        const dateStart  = new Date(course.useDate + "T00:00:00.000+09:00").toISOString();
        const dateEnd    = new Date(course.useDate + "T23:59:59.999+09:00").toISOString();
        whereObj = {
          [codeField]: { $regex: `^${codePrefix}` },
          createdAt: { $gte: { __type:"Date", iso:dateStart }, $lte: { __type:"Date", iso:dateEnd } },
        };
      } else {
        // 스파이x/침묵의방: createdAt KST 기준 날짜 필터
        const codePrefix = `${prog.prefix}${roundAlpha}`;
        const dateStart  = new Date(course.useDate + "T00:00:00.000+09:00").toISOString();
        const dateEnd    = new Date(course.useDate + "T23:59:59.999+09:00").toISOString();
        whereObj = {
          [codeField]: { $regex: `^${codePrefix}` },
          createdAt: { $gte: { __type:"Date", iso:dateStart }, $lte: { __type:"Date", iso:dateEnd } },
        };
      }

      const where = JSON.stringify(whereObj);

      const qs  = new URLSearchParams({ where, order: "-score", limit: 200 }).toString();
      const res = await fetch(`${BASE}/classes/${course.tableId}?${qs}`, { headers });
      const data = await res.json();

      console.log("🔍 참여자 조회 결과:", (data.results || []).length, "명");
      setPtcps((data.results || []).map(o => ({ objectId: o.objectId, ...o })));
    } catch (e) {
      console.warn("참여자 로드 실패:", e.message);
      setPtcps([]);
    } finally {
      setPLoad(false);
    }
  };

  /* 코드 현황 로드 — codeTable에서 useDate + round 기준 */
  const loadCodes = async () => {
    if (!prog?.codeTable) { setCodes([]); return; }
    setCLoad(true);
    try {
      const result = await API.codes(prog).listByCourse(course.useDate, course.round);
      setCodes(result);
    } catch { setCodes([]); }
    finally { setCLoad(false); }
  };

  useEffect(() => {
    loadPtcps();
    loadCodes();
  }, [course.objectId]);

  const delPtcp = async (p) => {
    if (!window.confirm(`"${p.userCode}" 를 삭제하시겠습니까?`)) return;
    try {
      await API.ptcp(prog).remove(course.tableId, p.objectId);
      toast("삭제되었습니다."); loadPtcps();
    } catch (e) { toast("삭제 실패: " + e.message, "error"); }
  };

  // 점수 있는 사람은 점수순, 없는 사람은 코드순
  const sorted = useMemo(() => {
    const cf = prog?.codeField || "introInCode";
    const hasScore = ptcps.filter(p => p.score != null).sort((a,b) => (b.score||0)-(a.score||0));
    const noScore  = ptcps.filter(p => p.score == null).sort((a,b) => (a[cf]||"").localeCompare(b[cf]||""));
    return [...hasScore, ...noScore];
  }, [ptcps]);

  // 코드 통계
  const usedCodes   = codes.filter(c => (c.usageCount || 0) >= 1);
  const unusedCodes = codes.filter(c => (c.usageCount || 0) < 1);

  const hasRole = prog?.roles?.length > 0;

  /* 메타 정보 — 테이블ID 숨김 */
  const metaItems = [
    { label:"프로그램", value: course.program,       highlight: true },
    { label:"회차",     value: `${course.round}회차`                 },
    { label:"코드 생성 수",     value: `${course.capacity}개`                },
    { label:"시간",     value: `${course.duration}분`                },
    { label:"날짜",     value: course.useDate                        },
    { label:"접두어",   value: prog?.prefix || "—",   code: true     },
  ];

  /* 컬럼 구성 (역할 있는 프로그램만 역할 컬럼 추가) */
  const hasTeam  = ptcps.some(p => p[prog?.teamField || "introTeam"]);
  /* 컬럼: 코드 | 팀명(있을때) | 닉네임 | 점수 | 클리어시간 | 등수 | 역할 */
  const colTemplate = [
    '110px',               // 코드
    hasTeam ? '80px' : null, // 팀명
    '1fr',                 // 닉네임
    '70px',                // 점수
    '90px',                // 클리어시간
    '50px',                // 등수
    hasRole ? '70px' : null, // 역할
  ].filter(Boolean).join(' ');

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", background:C.surface }}>

      {/* ── 헤더 ── */}
      <div style={{ flexShrink:0, background:C.surface, borderBottom:`1px solid ${C.border}` }}>

        {/* 1열: 목록으로 / 수정 */}
        <div style={{ padding:"18px 24px 0", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <button onClick={onClose}
            style={{ display:"flex", alignItems:"center", gap:8, padding:"9px 20px", borderRadius:10, background:C.accent, color:"#fff", border:"none", cursor:"pointer", fontSize:14, fontWeight:700, fontFamily:"inherit", boxShadow:"0 4px 14px rgba(92,124,206,.35)", transition:"all .15s" }}
            onMouseEnter={e => { e.currentTarget.style.background=C.accentDim; }}
            onMouseLeave={e => { e.currentTarget.style.background=C.accent; }}
          >← 목록으로</button>
          <Btn variant="ghost" onClick={() => setShowEdit(true)}>수정</Btn>
        </div>

        {/* 2열: 과정 제목 */}
        <div style={{ padding:"14px 24px 0" }}>
          <div style={{ fontSize:22, fontWeight:800, color:C.t1, letterSpacing:"-.6px" }}>{course.courseName}</div>
        </div>

        {/* 3열: 메타 강조 */}
        <div style={{ padding:"12px 24px 20px", display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
          {metaItems.map(({ label, value, highlight, code }, idx) => (
            <div key={label} style={{ display:"flex", alignItems:"center", gap:6 }}>
              <span style={{ fontSize:12, color:C.t3, fontWeight:500 }}>{label}</span>
              {highlight ? (
                <span style={{ background:C.accent, color:"#fff", padding:"4px 13px", borderRadius:20, fontSize:13, fontWeight:700, boxShadow:"0 2px 8px rgba(92,124,206,.3)" }}>{value}</span>
              ) : code ? (
                <code style={{ background:"#f0f4f3", color:C.accentTxt, padding:"4px 10px", borderRadius:6, fontSize:12, fontWeight:600, fontFamily:"monospace", border:`1px solid ${C.border2}` }}>{value}</code>
              ) : (
                <span style={{ fontSize:13, color:C.t1, fontWeight:600, background:C.bg, padding:"4px 10px", borderRadius:8, border:`1px solid ${C.border}` }}>{value}</span>
              )}
              {idx < metaItems.length - 1 && <span style={{ color:C.border2 }}>·</span>}
            </div>
          ))}
        </div>
      </div>

      {/* ── 스크롤 영역 ── */}
      <div style={{ flex:1, overflowY:"auto", padding:"24px" }}>

        {/* ── 코드 현황 섹션 ── */}
        {prog?.codeTable && (
          <div style={{ marginBottom:28 }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
              <div>
                <div style={{ fontSize:16, fontWeight:700, color:C.t1, letterSpacing:"-.3px" }}>코드 현황</div>
                <div style={{ fontSize:12, color:C.t3, marginTop:5 }}>
                  {cLoad ? "로딩 중…" : `전체 ${codes.length}개 · 사용 ${usedCodes.length}개 · 미사용 ${unusedCodes.length}개`}
                </div>
              </div>
              <Btn variant="ghost" onClick={loadCodes}>↻</Btn>
            </div>

            {/* 요약 카드 */}
            {!cLoad && codes.length > 0 && (
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:14 }}>
                {[
                  { label:"전체", val:codes.length,        bg:C.bg,        color:C.t1        },
                  { label:"사용", val:usedCodes.length,    bg:"#fff1f3",   color:C.danger    },
                  { label:"미사용",val:unusedCodes.length, bg:C.accentBg,  color:C.accentTxt },
                ].map(({ label, val, bg, color }) => (
                  <div key={label} style={{ background:bg, border:`1px solid ${C.border}`, borderRadius:10, padding:"12px 14px", textAlign:"center" }}>
                    <div style={{ fontSize:22, fontWeight:800, color, letterSpacing:"-1px" }}>{val}</div>
                    <div style={{ fontSize:12, color:C.t3, marginTop:4 }}>{label}</div>
                  </div>
                ))}
              </div>
            )}

            {/* 코드 칩 목록 */}
            {!cLoad && (
              <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                {codes.map(c => {
                  const used = (c.usageCount || 0) >= 1;
                  return (
                    <span key={c.objectId || c.entryCode} style={{
                      fontSize:12, fontFamily:"monospace", fontWeight:600,
                      padding:"4px 10px", borderRadius:6,
                      background: used ? "#fff1f3" : C.accentBg,
                      color:      used ? C.danger   : C.accentTxt,
                      border:     `1px solid ${used ? "#fdc5ce" : C.border2}`,
                    }}>
                      {c.entryCode}{used ? " ✓" : ""}
                    </span>
                  );
                })}
                {cLoad && <span style={{ fontSize:12, color:C.t3 }}>불러오는 중…</span>}
                {!cLoad && codes.length === 0 && <span style={{ fontSize:12, color:C.t3 }}>생성된 코드가 없습니다.</span>}
              </div>
            )}

            <div style={{ borderBottom:`1px solid ${C.border}`, marginTop:22 }}/>
          </div>
        )}

        {/* ── 참여자 리스트 타이틀 ── */}
        <div style={{ display:"flex", alignItems:"flex-end", justifyContent:"space-between", marginBottom:20 }}>
          <div>
            <div style={{ fontSize:16, fontWeight:700, color:C.t1, letterSpacing:"-.3px" }}>게임 참여자</div>
            <div style={{ fontSize:12, color:C.t3, marginTop:5 }}>
              게임을 진행한 참여자만 표시됩니다 · {ptcps.length}명
            </div>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <Btn variant="ghost" onClick={loadPtcps}>↻</Btn>
            <Btn onClick={() => setShowAddP(true)}>+ 수동 추가</Btn>
          </div>
        </div>

        {/* 컬럼 헤더 */}
        <div style={{
          display:"grid", gridTemplateColumns:colTemplate,
          gap:0, padding:"10px 16px",
          background:"#f4f5f9", borderRadius:10, marginBottom:4,
          fontSize:11, color:C.t3, fontWeight:700, letterSpacing:".5px",
        }}>
          <span>코드</span>
          {hasTeam && <span>팀명</span>}
          <span>닉네임</span>
          <span style={{textAlign:"right"}}>점수</span>
          <span style={{textAlign:"center"}}>클리어시간</span>
          <span style={{textAlign:"center"}}>등수</span>
          {hasRole && <span>역할</span>}
        </div>

        {pLoad ? (
          <div style={{ textAlign:"center", color:C.t3, padding:"3rem 0", fontSize:14 }}>불러오는 중…</div>
        ) : sorted.length === 0 ? (
          <div style={{ textAlign:"center", color:C.t3, padding:"3rem 0", fontSize:14 }}>참여자가 없습니다.</div>
        ) : sorted.map((p, i) => {
          const hasData  = p.score != null;
          const rank     = hasData ? i + 1 : null;
          const rc       = rank===1?"#f9a825":rank===2?"#9e9e9e":rank===3?"#bf8e59":C.t3;
          const codeField = prog?.codeField || "introInCode";
          const nameField = prog?.nameField || "playerName";
          const teamField = prog?.teamField || "introTeam";
          const nickName  = p[nameField] || p.playerName || p.nickname || null;
          const teamVal   = p[teamField] || null;
          const codeVal   = p[codeField] || p.introInCode || null;

          return (
            <div key={p.objectId}
              style={{ display:"grid", gridTemplateColumns:colTemplate, gap:0, padding:"12px 16px", borderRadius:10, marginBottom:2, alignItems:"center", cursor:"default", transition:"background .1s", borderBottom:`1px solid ${C.border}` }}
              onMouseEnter={e => e.currentTarget.style.background = C.hover}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              {/* 코드 */}
              <code style={{ fontSize:12, color:C.accentTxt, fontFamily:"monospace", fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                {codeVal || "—"}
              </code>

              {/* 팀명 — 텍스트만 */}
              {hasTeam && (
                <span style={{ fontSize:12, color: teamVal ? C.t2 : C.t3, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                  {teamVal || "—"}
                </span>
              )}

              {/* 닉네임 */}
              <span style={{ fontSize:13, fontWeight:600, color: nickName ? C.t1 : C.t3, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                {nickName ?? "—"}
              </span>

              {/* 점수 */}
              <span style={{ fontSize:13, fontWeight:700, color: hasData ? C.t1 : C.t3, textAlign:"right" }}>
                {p.score != null ? p.score.toLocaleString() : "—"}
              </span>

              {/* 클리어시간 */}
              <span style={{ fontSize:12, color: p.clearTime != null ? C.t2 : C.t3, textAlign:"center" }}>
                {p.clearTime != null ? fmtTime(p.clearTime) : "—"}
              </span>

              {/* 등수 */}
              <span style={{ fontSize:13, fontWeight:700, color: rank ? rc : C.t3, textAlign:"center" }}>
                {rank ?? "—"}
              </span>

              {/* 역할 */}
              {hasRole && (
                <span style={{ fontSize:11, color: p.userRole ? C.accentTxt : C.t3, background: p.userRole ? C.accentBg : "transparent", padding:"3px 8px", borderRadius:6, textAlign:"center", fontWeight:600 }}>
                  {p.userRole ?? "—"}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {showEdit && (
        <EditCourseModal
          course={course}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); onClose("refresh"); }}
          toast={toast}
        />
      )}
      {showAddP && (
        <AddParticipantModal
          course={course}
          onClose={() => setShowAddP(false)}
          onSaved={() => { setShowAddP(false); loadPtcps(); }}
          toast={toast}
        />
      )}
    </div>
  );
}

/* ================================================================
   ReportPage
   ================================================================ */